import { put, takeLatest, all, select, call } from 'redux-saga/effects';

import { logger } from 'src/error';
import { getIsHotelOnly } from 'src/store/view/selectors';
import { api } from 'src/api';
import { getClientAuthToken } from 'src/store/client/selectors';
import { clientStorage } from 'src/api/storage';
import {
  touristValidationFailedAction,
  touristValidationSuccessAction,
  touristFieldValidationSuccessAction,
  touristFieldValidationFailedAction,
  validationFailedAction,
  validationSuccessAction,
} from 'checkout/store/validation/actions';
import {
  areTouristsEqual,
  isChild,
  transformSavedTourists,
} from 'src/components/Tourists/utils';
import {
  touristsSelector,
  touristSelector,
  editedTouristsSelector,
} from 'src/store/tourists/selectors';
import { touristIsValidSelector } from 'src/store/touristsValidation/selectors';
import {
  setTouristValidatedAction,
  addSavedTourist,
  fetchSavedTourists,
  onTouristClickAction,
  submitTourists as submitTouristsAction,
  confirmTouristsAction,
  setEmptyTouristsAction,
} from 'src/routes/Checkout/store/actions/tourists';
import { validateTourist } from 'src/store/touristsValidation/saga.ts';
import { changeTouristData } from 'src/store/tourists/slice';
import { NATIONALITY_CONSTANTS } from 'src/constants';
import {
  getTouristValidationSchema,
  getTouristsValidationSchema,
  getTouristValidationSchemaHotelCheckout,
  getTouristsValidationSchemaHotelCheckout,
} from '../selectors/tourists';

import {
  VALIDATE_TOURIST_FIELD,
  SUBMIT_TOURIST,
  UPDATE_MOCK_TOURIST,
} from '../constants/tourists';
import { getPackageId, getTouristsCount } from '../selectors/package';
import {
  expandTouristFormAction,
  shrinkTouristFormsAction,
  goToTouristsHotelCheckoutAction,
  goToTouristsAction,
  routeTransitionEndAction,
} from '../actions/view';
import { prebuildOrderAction } from '../actions/order';

import {
  touristChangesOrder,
  submitTouristChange,
} from '../../../OrderChangingRequest/store/domains/order';
import { getCitizenshipsWithCode, getSavedTourists } from '../selectors/data';
import { setTouristsAction } from '../client/slice';
import { getIsTouristsPath } from '../selectors/router';
import { setCustomerFormShown } from '../domains/customer/slice';
import { HAS_CUSTOMER_FORM } from '../../CheckoutRoutes/PackageCheckout/components/Customer/abFeatures';
import { isCustomerInfoValidSelector } from '../domains/customer/selectors';
import { getActualizationShowModalCustomer } from '../selectors/actualization';
import { getIsSelectedInsurance } from '../domains/package-upsale-options/selectors';
import { setTouristFormLoading } from '../reducers/view';

function* getValidationSchema() {
  const isHotelOnly = yield select(getIsHotelOnly);
  const isSelectedInsurance = yield select(getIsSelectedInsurance);
  return isHotelOnly && !isSelectedInsurance
    ? yield select(getTouristValidationSchemaHotelCheckout)
    : yield select(getTouristValidationSchema);
}

export function* validateTouristBySchema(tourist) {
  const schema = yield* getValidationSchema();
  const result = yield call(() =>
    schema.validate(tourist, { abortEarly: false }),
  );
  return result;
}

function* validateTouristField(field, touristInfo) {
  const schema = yield* getValidationSchema();
  const result = yield call(() => schema.validateAt(field, touristInfo));
  return result;
}

export function* validateTourists(tourists) {
  const isHotelOnly = yield select(getIsHotelOnly);
  const isSelectedInsurance = yield select(getIsSelectedInsurance);
  const schema =
    isHotelOnly && !isSelectedInsurance
      ? yield select(getTouristsValidationSchemaHotelCheckout)
      : yield select(getTouristsValidationSchema);

  const result = yield call(() =>
    schema.validate(tourists, { abortEarly: false }),
  );
  return result;
}

function* submitTouristChangeHandler(action) {
  try {
    const editedTourists = yield select(editedTouristsSelector);
    yield call(validateTourists, editedTourists);
    yield put(touristChangesOrder(action.payload));
  } catch (err) {
    if (err.inner) {
      yield put(validationFailedAction(err.inner));
    }
  }
}

function* submitTourists() {
  try {
    const tourists = yield select(touristsSelector);
    const isHotelOnly = yield select(getIsHotelOnly);
    yield call(validateTourists, tourists);
    yield put(validationSuccessAction(tourists));

    // TODO: feature customer_form - убрать логику с HAS_CUSTOMER_FORM
    const showModalCustomer = yield select(getActualizationShowModalCustomer);
    // На отельном чекауте сразу переходим к оплате
    // Если форма покупателя выключена, то переходим к оплате сразу
    if (isHotelOnly || !HAS_CUSTOMER_FORM || !showModalCustomer) {
      yield put(confirmTouristsAction());
    }
    const customerValid = yield select(isCustomerInfoValidSelector);
    if (customerValid) {
      yield put(confirmTouristsAction());
    }
  } catch (err) {
    if (err.inner) {
      yield put(validationFailedAction(err.inner));
    }
  }
}

function* handleTouristFieldValidation(action) {
  const {
    payload: { index, field },
  } = action;
  try {
    const touristInfo = yield select(touristSelector, index);
    yield call(validateTouristField, field, touristInfo);
    yield put(touristFieldValidationSuccessAction({ index, field }));
  } catch (error) {
    yield put(touristFieldValidationFailedAction({ index, field, error }));
  }
}

function* handleTouristSubmit(action) {
  try {
    const { payload: index } = action;

    const touristInfo = yield select(touristSelector, index);
    try {
      yield call(validateTouristBySchema, touristInfo);
      yield put(touristValidationSuccessAction({ index, touristInfo }));
    } catch (err) {
      yield put(touristValidationFailedAction({ errors: err.inner, index }));
      return;
    }
    // TODO: пока вынужденный костыль поправлю в ближайшее время
    yield call(validateTourist);

    const showModalCustomer = yield select(getActualizationShowModalCustomer);
    yield put(setTouristValidatedAction(index));
    const touristsCount = yield select(getTouristsCount);
    const isLastTourist = touristsCount === index + 1;
    const isValid = yield select(touristIsValidSelector, index);
    if (!isValid) {
      return;
    }

    if (isLastTourist) {
      yield put(validationSuccessAction());

      // TODO: feature customer_form - убрать логику с HAS_CUSTOMER_FORM
      if (HAS_CUSTOMER_FORM && showModalCustomer) {
        yield put(shrinkTouristFormsAction());
        yield put(setCustomerFormShown(true));
      } else {
        yield put(confirmTouristsAction());
      }
    } else {
      yield put(expandTouristFormAction(index + 1));
    }
  } catch (error) {
    logger(error, 'Tourist');
  }
}

function* handleMockTouristUpdated() {
  yield put(prebuildOrderAction());
}

function* handleSaveTourist(action) {
  const isHotelOnly = yield select(getIsHotelOnly);
  const isSelectedInsurance = yield select(getIsSelectedInsurance);

  // Если сокращенная форма туристов, то не запоминаем данные туристов
  if (isHotelOnly && !isSelectedInsurance) return;

  try {
    const authToken = yield select(getClientAuthToken);
    const {
      gender,
      name,
      surname,
      patronymic,
      nationality_id,
      birthday,
      docType,
      passport_number,
      passport_issued_on,
      passport_expires_on,
      age,
    } = action.payload.touristInfo;

    const savedTourists = yield select(getSavedTourists);

    const alreadySaved = savedTourists?.some((savedTourist) =>
      areTouristsEqual(savedTourist, action.payload.touristInfo),
    );

    if (alreadySaved) return;

    const params = {
      tourist: {
        child: isChild(age),
        gender,
        name,
        surname,
        patronymic,
        nationality_id,
        birthday,
        doc_type: docType.toLocaleLowerCase(),
        passport_number,
        passport_issued_on,
        passport_expires_on,
      },
      auth_token: authToken,
    };

    const response = yield api.postClientTourists(params);

    if (!response?.success) {
      throw new Error('postClientTourists error', response.error);
    } else {
      yield put(addSavedTourist(response.tourist));
    }
  } catch (e) {
    logger(e, 'Tourist');
  }
}

function* handleRouteChanged() {
  const isTouristsPath = yield select(getIsTouristsPath);

  if (isTouristsPath) {
    yield put(fetchSavedTourists());
  }
}

export function* handleFetchSavedTourists() {
  try {
    const authToken = clientStorage.getAuthToken();
    if (!authToken) {
      return;
    }
    const tourists = yield api.getClientTourists({
      auth_token: authToken,
    });

    if (tourists) {
      yield put(setTouristsAction(transformSavedTourists(tourists)));
    }
  } catch (e) {
    logger(e, 'handleConfirmClient: Failed to load tourists');
  }
}

function* handleOnTouristClick(action) {
  const isHotelOnly = yield select(getIsHotelOnly);
  if (isHotelOnly) {
    yield put(goToTouristsHotelCheckoutAction(action.payload));
  } else {
    yield put(goToTouristsAction(action.payload));
  }
}

function* handleSetEmptyTourists() {
  const packageId = yield select(getPackageId);
  try {
    yield put(setTouristFormLoading(true));
    const docTypes = yield api.getTouristsAvailableDocTypes({
      package_id: packageId,
    });
    const touristsCount = yield select(getTouristsCount);
    const tourists = yield select(touristsSelector);
    const citizenships = yield select(getCitizenshipsWithCode);

    for (let idx = 0; idx < touristsCount; idx += 1) {
      const availableDocTypes = Object.keys(docTypes).reduce((acc, key) => {
        const result = { ...acc };
        result[key] = docTypes[key][idx];
        return result;
      }, {});
      const tourist = tourists[idx] ?? {};
      const nationalityId = tourist?.nationality_id ?? NATIONALITY_CONSTANTS.RU;
      const citizenshipCode = citizenships[nationalityId];
      yield put(
        changeTouristData({
          index: idx,
          data: {
            ...tourist,
            // на отельном чекауте данные пользователя подставляются автоматически в туристов
            // поэтому массив туристов не пустой и турист по индексу может быть undefind
            docType:
              citizenshipCode && availableDocTypes[citizenshipCode]
                ? availableDocTypes[citizenshipCode][0]
                : availableDocTypes.OTHER[0],
            nationality_id: nationalityId,
            availableDocTypes,
          },
        }),
      );
    }
    yield put(setTouristFormLoading(false));
  } catch (error) {
    console.log(error);
  }
}

export default function* touristSaga() {
  yield all([
    takeLatest(setEmptyTouristsAction.type, handleSetEmptyTourists),
    takeLatest(submitTouristsAction, submitTourists),
    takeLatest(submitTouristChange, submitTouristChangeHandler),
    takeLatest(SUBMIT_TOURIST, handleTouristSubmit),
    takeLatest(VALIDATE_TOURIST_FIELD, handleTouristFieldValidation),
    takeLatest(UPDATE_MOCK_TOURIST, handleMockTouristUpdated),
    takeLatest(touristValidationSuccessAction, handleSaveTourist),
    takeLatest(routeTransitionEndAction, handleRouteChanged),
    takeLatest(fetchSavedTourists.type, handleFetchSavedTourists),
    takeLatest(onTouristClickAction.type, handleOnTouristClick),
  ]);
}
